import { useSyncExternalStore } from 'react';
import { Navigate, useLocation } from 'react-router';
import {
  getCustomer,
  getCustomerToken,
  subscribeCustomer,
} from './customer-auth';

/**
 * Subscribes a component to the localStorage-backed customer auth
 * store. Returns the live `{ token, user }` pair — re-renders when
 * either changes.
 */
export function useCustomerAuth() {
  const token = useSyncExternalStore(
    subscribeCustomer,
    getCustomerToken,
    () => null,
  );
  const user = useSyncExternalStore(
    subscribeCustomer,
    getCustomer,
    () => null,
  );
  return { token, user, isAuthenticated: !!token };
}

/**
 * Guard for routes that require a logged-in customer. Bounces to
 * the customer sign-in page when no token is present.
 *
 * Crucially, we forward the page the guest was trying to reach as
 * `state.from` so SignInForm / the signup chain resume them there
 * after they authenticate — instead of dumping them on /user-landing
 * (the homepage). This is what keeps a guest who hits checkout mid
 * booking flow (e.g. the beauty/hair flow lands on /your-appointment,
 * which is guarded here) from losing their basket and being kicked to
 * the home screen after signing in. Only the cleaning wizard passed
 * `from` explicitly before; routing it through the guard covers every
 * flow at once.
 */
export function RequireCustomer({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isAuthenticated } = useCustomerAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/signin/customer"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  return <>{children}</>;
}

/**
 * Guard for routes that require a guest (not authenticated).
 * Bounces authenticated customers to home page.
 */
export function RequireGuest({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isAuthenticated } = useCustomerAuth();
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
